import * as Html from "./Html.ts";
import * as amplify from "./amplify.ts";
import * as i18n from "../i18n.ts";
import ApiServer from "../ApiServer.ts";
import MultiplexApi from "../server/MultiplexApi.ts";
import * as TPL_snack from "../modal/snack.mustache";
import Icons, { SvgFile } from "../svg/Icons.ts";
import { ArrayQueue } from "../lib/ArrayQueue.ts";
import { markValidNative, markInvalidNative, isDirty } from "./forms.ts";
import * as libLocation from "../lib/location.ts";


export type Modal = JQuery&{amplifyEvents:namedFunctions,onClose:Function,onConfirm:Function}
export type DomModal = HTMLElement&{
  amplifyEvents:namedFunctions,
  onClose:Function,
  onConfirm:Function,
  //called before the user closes the modal, return false to cancel the closing
  onCancel?:()=>Promise<boolean>|boolean,
  _dismiss?:()=>void
}

type modalMethod = (this:DomModal,arg?:any)=>void;
type Lottie = "images/lottie/hugging_face.json"
  |"images/lottie/face_with_monocle.json"
  |"images/lottie/robot.json"
  |"images/lottie/anim_clock.json"
  |"images/lottie/flying_saucer.json"
  |"images/lottie/anim_files_tutorial_top_easy_sharing.json"
  |"images/lottie/anim_files_tutorial_top_folder_color.json"
  |"images/lottie/anim_files_tutorial_top_access_control.json"
  |"images/lottie/anim_storage_full.json"
  |"images/lottie/anim_lock.json"
  |"images/lottie/face_with_rolling_eyes.json"
  |"images/lottie/crying_face.json"
  |"images/lottie/anim_verified_check.json"
  |"images/lottie/anim_teddy_bear.json"
  |"images/lottie/dizzy_face.json"
  |"images/lottie/anim_warning.json"
  |"images/lottie/money_with_wings.json"
  |"images/lottie/53775.json"
type ModalMainImage = (
  {
    lottie:Lottie,
    mediaSize?:number,
  }|{
    image:string,
    mediaSize?:number,
  }|{
    svg:SvgFile,
    mediaSize?:number,
  }|{
    emoji:string,
    mediaSize?:number,
  }|
  {}
)
var
  popups : DomModal[] = [],
  snacks : {node:HTMLElement,onClose:Function}[] = [];

/**
 * DOM-native version of openModal - returns HTMLElement instead of jQuery
 * Use this for progressive migration away from jQuery
 */
export function openDomModal(name :MUS,data :any = {},amplifyEvents = {}): DomModal {
  var modal :DomModal,
    body = document.body;


  modal = Html.setHtmlAtEndDom(body,name,data) as DomModal;
  modal.scrollIntoView(false);
  popups.push(modal);

  modal.amplifyEvents = amplifyEvents;
  for(var i in modal.amplifyEvents){
    amplify.subscribe(i,modal.amplifyEvents[i]);
  }

  modal._dismiss = libLocation.pushOverlay(()=>{
    closeDomModal(modal);
  });

  return modal;
}


export type ToastButton = {title:string,onclick?:EventListener}
type ToastInput = {
  svg?:SvgFile,
  icon?:SvgFile,
  emoji?:string,
  text:string,
  onclick?:EventListener
  button?:ToastButton[]
}
//why the toast went away: its timeout, a click on it or its cross, close() called by code, or a click on one of
//its buttons, given back as it was passed in
export type ToastClosed = {reason:'auto'|'dismiss'|'code', button?:undefined} | {reason:'button', button:ToastButton}
type ToastOutput = {
  domNode:HTMLElement,
  close:()=>void,
  closed:Promise<ToastClosed>
}

const DISPLAYED_TOASTS = new ArrayQueue<ToastOutput>()
//a toast speaks of the family being left: an undo would apply to the wrong one
amplify.subscribe("familyScopeChange sessionExpired",()=>{
  [...DISPLAYED_TOASTS.array].forEach(displayed=>displayed?.close());
});
export async function toast(data :ToastInput, autoclose ?:number|undefined ) : Promise<ToastOutput>{
  if(autoclose===undefined){
    autoclose = data.button?6000:3000
  }
  if(data.text){
    data = {...data, text:i18n.get(data.text)}
  }
  // Extract emoji from text if no emoji/icon/svg is provided
  if(!data.emoji && !data.icon && !data.svg && data.text){
    const emojiMatch = data.text.match(/^(\p{Emoji_Presentation}|\p{Emoji}️)/u);
    if(emojiMatch){
      data = {...data, emoji: emojiMatch[0], text: data.text.slice(emojiMatch[0].length).trim()};
    }
  }

  let
    TPL = await import("../modal/toast_simple.mustache"),
    domNode = Html.setHtmlAtEndDom(document.body,TPL,data),
    resolveClose : (value:ToastClosed)=>void,
    closed = new Promise<ToastClosed>((resolve,reject)=>resolveClose=resolve),
    output = {domNode,closed,close:()=>closeBy({reason:'code'})},
    closeTimer :number,
    buttons = [...domNode.querySelectorAll('a')];

  var index = DISPLAYED_TOASTS.push(output);
  domNode.style.bottom = (7*(index+1)-2)+'%';

  function slowClose(){
    domNode.classList.add('fadeOutDown');
    closeTimer = setTimeout(()=>closeBy({reason:'auto'}),1000)
  }

  function closeBy(closedBy :ToastClosed){
    if(!domNode.isConnected){
      return;
    }
    clearTimeout(closeTimer)
    Html.cleanHtml(domNode);
    domNode.remove();
    DISPLAYED_TOASTS.remove(output);
    resolveClose(closedBy);
  }
  //a button closes the toast when its click bubbles up here, unless its handler stops it to keep the toast
  domNode.addEventListener('click',(evt)=>{
    const button = data.button?.[buttons.indexOf((evt.target as Element).closest('a'))];
    closeBy(button?{reason:'button',button}:{reason:'dismiss'});
  })
  if(data.onclick) {
    domNode.querySelector('flexauto')?.addEventListener('click',data.onclick)
  }
  data.button?.forEach((button,idx)=>{
    if(button.onclick){
      buttons[idx].addEventListener('click',button.onclick)
    }
  })
  if(autoclose!==0){
    closeTimer = setTimeout(slowClose,autoclose)
  }
  return output;

}
/**
 * A toast offering to undo what was just done, resolved once it is gone: true when Undo was clicked.
 */
export async function toastUndo(data :Omit<ToastInput,'button'>) :Promise<boolean>{
  const undo :ToastButton = {title:'actions.undo'};
  const {closed} = await toast({...data,button:[undo]});
  return (await closed).button===undo;
}
export function toastDone(i18nKey :string,data :any = {},toastOptions :Partial<ToastInput> = {}){
  return toast(Object.assign({text:i18n.get(i18nKey,data),svg:Icons.ic_done},toastOptions))
}
export function closeDomModal(modal ? : DomModal){
  if(modal==undefined && popups.length>0){
    modal = popups[popups.length-1];
  }
  if(modal==undefined) return;
  if(popups.indexOf(modal)>-1){
    popups.splice(popups.indexOf(modal),1);
  }

  // Animate panel closure (slide out) before removing from DOM
  var pane = modal.querySelector('.pane-right.animated-width.open') as HTMLElement;
  if(pane){
    // Hide backdrop immediately so only the panel slide-out is visible
    modal.style.backgroundColor = 'transparent';
    pane.classList.remove('open');
    pane.addEventListener('transitionend', ()=> removeModal(modal), {once:true});
    setTimeout(()=> removeModal(modal), 350); // fallback if transitionend doesn't fire
  }else{
    removeModal(modal);
  }
}

// Actual DOM cleanup — guarded against double-call (transitionend + setTimeout)
function removeModal(modal : DomModal){
  if(!modal.isConnected) return;
  Html.cleanHtml(modal);
  modal.remove();
  for(var i in modal.amplifyEvents){
    amplify.unsubscribe(i,modal.amplifyEvents[i]);
  }
  if(modal._dismiss){
    modal._dismiss();
  }
  if(modal.onClose){
    modal.onClose();
  }
}

export function closeSnack(){
  snacks.forEach(snack=>{
    snack.node.style.opacity = "0";
    snack.onClose()
    setTimeout(function(){snack.node.remove()},500)
  })
}
/**
 * Confirmation to use as the onCancel of a modal holding an editing form : while the form has
 * pending changes, closing the modal asks the user to discard or to save them first.
 * Only saving keeps the modal open, the time of the save
 *
 * @param form - the form being edited
 * @returns false when the closing of the modal has to be cancelled
 */
export async function askBeforeDiscarding(form :HTMLFormElement){
  if(!form || !isDirty(form)) return true;

  var answer = await askAsPromise({
    title:'discardChanges.title',
    message:'discardChanges.message',
    lottie:'images/lottie/anim_warning.json',
    button:[
      {title:'discardChanges.discard',value:'discard',class:"negative"},
      {title:'discardChanges.save',value:'save'}
    ]
  });
  if(answer==='save'){
    //the modal is closed by the amplify event published once the save succeeded
    return false;
  }
  //closing the confirmation itself discards the changes as well
  return true;
}

Html.registerDom({
  async modalclose(after:string){
    var closing = popups[popups.length-1];
    if(closing?.onCancel && (await closing.onCancel())===false) return;
    closeDomModal(closing);
    if(after){
      Html.trigger(this,after,Array.prototype.slice.call(arguments,1));
    }
  },
  snackclose:closeSnack,
  modalanswer(answer){
    if(typeof answer === 'string' && answer.length===0){
      answer = null;
    }
    let popup = popups[popups.length-1];
    popup.onConfirm(answer);
    closeDomModal(popup);

  }
});

/*
  Function: openDialog(type,message,onConfirm): object
  Open a modal dialog with given title, message and optional callbacks

  Parameters:
    type - string, the name of the mustache modal for the dialog,
      one of the constants OK, OK_CANCEL or YES_NO
    message - string, the text to display as message in the modal
    onConfirm - function, optional, callback called when the modal is closed
      with the confirm button

  Returns:
    object, jQuery object wrapping the new dialog
*/
function openDialog(tpl :MUS, message:any, onConfirm?:modalMethod, onClose ?:modalMethod){
  var modal = openDomModal(tpl,(typeof message === 'string')?{ message: message}:message);
  modal.onConfirm = onConfirm || function(){};
  modal.onClose = onClose;
  return modal;
}

/**
 * 
 * @param message display an oops message
 * @returns A promise that resolve when the oops is closed
 */
export function oops(message :SimpleInput|string){
  var simpleArg = {title:'errors.oops',lottie:"images/lottie/dizzy_face.json" as Lottie,button:"actions.ok",message:undefined};
  if(typeof message === 'object'){
    Object.assign(simpleArg,message)
  }else{
    simpleArg.message = message;
  }
  return simple(simpleArg)
}


type ConfirmInput = {
  title:string,
  message:string,
  yes:string,
  no:string,
  lottie:Lottie
}

export async function confirm(message :Partial<ConfirmInput>|string) : Promise<boolean>{
  var data :Partial<ConfirmInput> = {
    title:'new_confirm_action.title',
    yes:'actions.yes',
    no:'actions.no',
    message:''
  };
  if((typeof message === 'string')){
    data.message = message;
  }else{
    Object.assign(data,message);
  }
  return 'true' === await askAsPromise({
    title:data.title,
    message:data.message,
    lottie:data.lottie,
    button:[
      {title:data.yes,value:'true'},
      {title:data.no,value:'false',class:"negative"},
    ]
  })
}
export async function confirmDelete(message :string|Partial<ConfirmInput>) : Promise<boolean>{
  var data :Partial<ConfirmInput> = {
    title:'new_confirm_action.title',
    yes:'actions.delete',
    no:'actions.cancel',
    lottie:'images/lottie/anim_warning.json',
    message:''
  };
  if((typeof message === 'string')){
    data.message = message;
  }else{
    Object.assign(data,message);
  }
  return 'true' === await askAsPromise({
    title:data.title,
    messageHTML:i18n.get(data.message),
    lottie:data.lottie,
    button:[
      {title:data.yes,value:'true',class:"negative"},
      {title:data.no,value:'false',class:"secondary"},
    ]
  })
}

export type AskInput = ModalMainImage&{
  metaId?:string,
  title?:string,
  cover?:string,
  image?:string,
  mediaHTML?:string,
  h1?:string,
  h2?:string,
  message?:string,
  messageHTML?:string,
  sub1?:string,
  copyable?:string,
  checkbox?:Array<{title:string,checked?:boolean,onclick:Function}>,
  button:Array<{title:string,class?:"negative"|"secondary"|"premium",action?:string,value?:string,onclick?:()=>any}>
}

export async function ask(data :AskInput,onConfirm?:modalMethod){
  if(onConfirm===undefined){
    for(let i=0;i<data.button.length;i++){
      data.button[i].value = ""+i;
    }
    onConfirm = function(clicked){
      for(let i=0;i<data.button.length;i++){
        if(clicked===data.button[i].value){
          if(data.button[i].onclick) {
            data.button[i].onclick();
          }
          return;
        }
      }
    }
  }
  var TPL = await import("../modal/dialog_ask.mustache")
  var modal = openDialog(TPL,data,function(arg){
        onConfirm.call(this,arg);
        this.onClose=undefined
      },function(){
        onConfirm.call(this,null)
      });
  if(data.checkbox){
    modal.querySelectorAll('[type=checkbox]').forEach((item,index)=>{
      item.addEventListener('click',function(this:HTMLInputElement,event){
        data.checkbox[index].onclick(this.checked)
      })
    })
  }

  return modal;

}

export async function askAsPromise(data :AskInput){
  var tpl = await import("../modal/dialog_ask.mustache");

  return new Promise<string>((resolve,reject)=>{
    openDialog(tpl,data,function(this:DomModal,arg){resolve(arg);this.onClose=undefined},function(){
      resolve(null)
    });
  });
}

export async function askAsPromiseTpl(tpl :MUS|undefined ,data :any = {},domWidget?:{initDom:(el :HTMLElement)=>void,destroyDom?:(el :HTMLElement)=>void}){
  return new Promise((resolve,reject)=>{
    var dialog = openDialog(tpl,data,function(arg){
      resolve(arg);
      if(domWidget?.destroyDom) domWidget?.destroyDom(dialog)
      this.onClose=undefined
    },function(){
      if(domWidget?.destroyDom) domWidget?.destroyDom(dialog)
      resolve(null)
    });
    domWidget?.initDom(dialog)

  });
}


type PickButton = {
  title?:string,
  text?:string,
  icon?:SvgFile
  image?:string
}

type PickInput<T extends PickButton> = {
  title:string
  cssClass?:"large"
  gap?:string
  button :T[]
}

export async function pick<T>(data :PickInput<T>) :Promise<T>{
  var TPL = await import("../modal/dialog_pick.mustache")
  return new Promise((resolve,reject)=>{
    openDialog(TPL,data,function(arg){
      resolve(data.button[arg]);
      this.onClose=undefined
    },function(){
      resolve(null)
    });
  });
}


type SimpleInput = {
  title?:string
  lottie?:Lottie
  message:string
  button?:string
}

export async function simple(messageObject :SimpleInput){
  const data: AskInput = {
    title: messageObject.title,
    message: messageObject.message,
    lottie: messageObject.lottie,
    button: [
      { title: messageObject.button || 'actions.ok', value: 'true' }
    ]
  };

  const result = await askAsPromise(data);
  return result === 'true';
}

document.addEventListener('keyup', function ( e :KeyboardEvent ) {
  if(e.which===27){
    closeDomModal();
  }
});
amplify.subscribe("pageUnload",function(){
  while(popups.length>0){
    closeDomModal();
  }
});

export async function alert(message :string|Partial<AskInput>){
  var datas :AskInput = {
    button:[
      {title:"actions.ok"},
    ]
  };
  if((typeof message === 'string')){
    datas.message = message;
  }else{
    Object.assign(datas,message);
  }
  return askAsPromise(datas)

}
/**
 * Use only in specific case when we need the modal Node, prefer simple modal.alert()
 * @param message
 * @returns the modal
 */
export async function alertModal(message :string|Partial<AskInput>){
  var datas :AskInput = {
    button:[
      {title:"actions.ok"},
    ]
  };
  if((typeof message === 'string')){
    datas.message = message;
  }else{
    Object.assign(datas,message);
  }
  return openDialog(await import("../modal/dialog_ask.mustache"),datas)
}



export function snack(TPL :MUS,data :any,displayTime = 0){
  var
    snackNode = Html.setHtmlAtEnd(document.querySelector('.fw-layout'),TPL,data)[0],
    theSnack = {node:snackNode,onClose:()=>{}};
  setTimeout(function(){snackNode.style.opacity="1"},100)
  snacks.push(theSnack)
  if(displayTime){
    setTimeout(function(){closeSnack()},displayTime);
  }
  return theSnack;
}
export function snackDone(key :string){
  return snack(TPL_snack,{key:key},2000)
}

type AskTextInput = {
  title:string,
  isPassword?:boolean,
  h1?:string,
  h2?:string,
  placeholder:string,
  sub?:string,
  buttonName:string,
  validate?:(url:string)=>Promise<boolean>
} & ModalMainImage & (
  {message:string}|
  {messageHTML:string}
)

export async function askText(options :AskTextInput){
  return new Promise<string>(async (resolve,reject)=>{
    var
      url :string,
      theModal = openDomModal(await import("../modal/asktext.mustache"),options);

    const form = theModal.querySelector('form') as HTMLFormElement;
    form.addEventListener('submit', async function(this:HTMLFormElement,event:Event){
      event.preventDefault();
      if(!this.url.value) return;
      try{
        const isValid = (!options.validate || await options.validate(this.url.value));
        if(isValid){
          markValidNative(this.url);
          url = this.url.value;
          closeDomModal(theModal);
        }else{
          url = undefined;
        }
      }catch(e :any){
        console.log(e);
        markInvalidNative(this.url, e.message);
        url = undefined;
      }

    });
    theModal.onClose = ()=>resolve(url)
  })
}
export async function askIdentifierEmail(options :AskTextInput){
  return askText(Object.assign({
    async validate(email:string){
      var result = await ApiServer.log2check("Email",email)
      if(result===false){
        throw new Error(i18n.get("invitationJs.errorEmail"))
      }
      return result
    }
  },options))
}
export async function askCurrentPassword(identifier :string,options :AskTextInput){
  return askText(Object.assign({
    isPassword:true,
    async validate(password:string){
      return MultiplexApi.log2in(undefined,identifier,undefined,password)
    }
  },options))
}



type AskTextareaInput = {
  title:string,
  text:string,
  validate?:(url:string)=>Promise<boolean>
}

export async function askTextarea(options :AskTextareaInput){
  return new Promise<string>(async (resolve,reject)=>{
    var
      text :string,
      TPL = await import("../modal/asktextarea.mustache")
    var theModal = openDomModal(TPL,options);
    const form = theModal.querySelector('form') as HTMLFormElement;
    form.addEventListener('submit', async function(this:HTMLFormElement,event:Event){
      event.preventDefault();
      if(!this.text.value) return;
      var isValid = (!options.validate || await options.validate(this.text.value))
      if(isValid){
        text = this.text.value;
        closeDomModal(theModal);
      }else{
        text = undefined;
      }
    });
    theModal.onClose = ()=>resolve(text)
  })
}


export async function askLocation(options :AskTextInput){
  return new Promise<string>(async (resolve,reject)=>{
    var
      location :string,
      TPL = await import("../modal/asklocation.mustache")
    var theModal = openDomModal(TPL,options);
    const form = theModal.querySelector('form') as HTMLFormElement;
    form.addEventListener('submit', async function(this:HTMLFormElement,event:Event){
      event.preventDefault();
      location = this.location.value;
      closeDomModal(theModal)
    });
    theModal.onClose = ()=>resolve(location)
  })
}

export async function needpremium(options : {title :string, message:string}){
  return ask({
    title:options.title,
    message:options.message,
    lottie:"images/lottie/anim_lock.json",
    button:[
      {class:"premium",action:"modalclose|takepremium",title:"actions.gopremium"},
      {class:"secondary",title:"actions.later"},
    ]
  })
}

export function toastMaxAttachmentReach(limit){
  return toast({emoji:"ℹ️",text:i18n.get('infomessage.attachments_limit',limit)})
}

export async function pickAssignee(title :string) :Promise<{accountIds:string[], isToAll:boolean}>{
  var FwDatas = (await import("../FwDatas.ts"));
  var TPL = await import("../modal/pickassignee.mustache");
  return new Promise((resolve)=>{
    var theModal = openDomModal(TPL, {title, family: FwDatas.getFamily()});
    theModal.onConfirm = function(){
      var select = theModal.querySelector('select[name="attendee"]') as HTMLSelectElement;
      var isToAll = (theModal.querySelector('input[name="isToAll"]') as HTMLInputElement).value === 'true';
      var accountIds = Array.from(select.selectedOptions).map(o=>o.value);
      resolve({accountIds, isToAll});
    };
    theModal.onClose = ()=>resolve(null);
  });
}

export async function pickColor(title :string) :Promise<string>{
  var TPL = await import("../modal/pickcolor.mustache");
  return new Promise((resolve)=>{
    var theModal = openDomModal(TPL, {title});
    theModal.onConfirm = function(){
      var input = theModal.querySelector('input[name="color"]') as HTMLInputElement;
      resolve(input?.value || null);
    };
    theModal.onClose = ()=>resolve(null);
  });
}

export async function onCancelForm(this :DomModal){
  var form = this.querySelector('form')
  if(await askBeforeDiscarding(form)){
    return true;
  }else{
    form.dispatchEvent(new Event('submit',{bubbles:true,cancelable:true}));
    return false;
  }
};
